import React, { createContext, useContext, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import {
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signInWithPopup,
  GoogleAuthProvider,
  signOut,
  onAuthStateChanged,
  sendPasswordResetEmail,
  sendEmailVerification,
  updateProfile
} from 'firebase/auth';
import { doc, setDoc, getDoc } from 'firebase/firestore';

// Safely import Firebase config
let auth = null;
let db = null;
try {
  const firebaseConfig = require('../config/firebase');
  auth = firebaseConfig.auth;
  db = firebaseConfig.db;
} catch (error) {
  console.warn('Firebase not configured yet. Authentication will be disabled.');
}

const AuthContext = createContext();

export function useAuth() {
  return useContext(AuthContext);
}

export function AuthProvider({ children }) {
  const [currentUser, setCurrentUser] = useState(null);
  const [userData, setUserData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [userDataLoading, setUserDataLoading] = useState(false);
  const [pendingNavigation, setPendingNavigation] = useState(null);

  // Check if authentication should be bypassed for development
  const skipAuth = process.env.REACT_APP_SKIP_AUTH === 'true';

  // Google Auth Provider
  const googleProvider = new GoogleAuthProvider();

  // Function to set pending navigation
  const setPendingNav = (path) => {
    setPendingNavigation(path);
  };

  // Sign up with email and password
  async function signup(
    email,
    password,
    displayName,
    role,
    barNumber,
    barState,
    organization
  ) {
    if (!auth) {
      console.error('Firebase auth is not initialized');
      throw new Error('Firebase not configured');
    }

    console.log('Creating user account for:', email);
    const result = await createUserWithEmailAndPassword(auth, email, password);
    console.log('User account created successfully:', result.user.uid);

    // Update the user's display name in Firebase Auth
    await updateProfile(result.user, { displayName });
    console.log('Display name updated to:', displayName);

    // Send email verification
    try {
      console.log('Attempting to send verification email to:', email);
      await sendEmailVerification(result.user);
      console.log('✅ Verification email sent successfully to:', email);
      console.log(
        'User should check their inbox (and spam folder) for the verification link'
      );
    } catch (error) {
      console.error('❌ Error sending verification email:', error);
      console.error('Error code:', error.code);
      console.error('Error message:', error.message);
      // Don't fail signup if verification email fails
    }

    // Store additional user data in Firestore if available
    if (db) {
      try {
        // Initialize trial for new user
        const now = new Date();
        const trialEnd = new Date(now.getTime() + 7 * 24 * 60 * 60 * 1000); // 7 days

        const userData = {
          displayName,
          email,
          role,
          barNumber: barNumber || null,
          barState: barState || null,
          organization: organization || null,
          createdAt: new Date().toISOString(),
          // Initialize usage tracking with trial
          usage: {
            searches: 0,
            exports: 0,
            notifications: 0,
            resetDate: now.toISOString(),
            trialStartDate: now.toISOString(),
            trialEndDate: trialEnd.toISOString()
          },
          usageUpdatedAt: new Date().toISOString()
        };

        const timeoutPromise = new Promise((_, reject) =>
          setTimeout(() => reject(new Error('Firestore timeout')), 5000)
        );

        const setDocPromise = setDoc(
          doc(db, 'users', result.user.uid),
          userData
        );
        await Promise.race([setDocPromise, timeoutPromise]);

        console.log('User document created with trial:', userData);

        // Return the user data along with the result
        return { ...result, userData };
      } catch (error) {
        console.error(
          'AuthContext: Error storing user data in Firestore:',
          error.message
        );
        throw error; // Re-throw to let the calling code handle it
      }
    } else {
      console.warn('AuthContext: No Firestore database available');
    }
    return result;
  }

  // Sign in with email and password
  function login(email, password) {
    if (skipAuth) {
      console.log('Login bypassed for development');
      // Return a mock user for development
      const mockUser = {
        uid: 'dev-user-123',
        email: 'dev@localhost.com',
        displayName: 'Development User',
        emailVerified: true,
        providerData: []
      };
      return Promise.resolve({ user: mockUser });
    }
    if (!auth) throw new Error('Firebase not configured');
    return signInWithEmailAndPassword(auth, email, password);
  }

  // Sign in with Google
  function loginWithGoogle() {
    if (skipAuth) {
      console.log('Google login bypassed for development');
      // Return a mock user for development
      const mockUser = {
        uid: 'dev-user-123',
        email: 'dev@localhost.com',
        displayName: 'Development User',
        emailVerified: true,
        providerData: [{ providerId: 'google.com' }]
      };
      return Promise.resolve({ user: mockUser });
    }
    if (!auth) throw new Error('Firebase not configured');
    return signInWithPopup(auth, googleProvider);
  }

  // Sign out
  function logout() {
    if (skipAuth) {
      console.log('Logout bypassed for development');
      // Clear the mock user
      setCurrentUser(null);
      setUserData(null);
      return Promise.resolve();
    }
    if (!auth) throw new Error('Firebase not configured');
    // Clear session storage so disclaimer shows on next sign-in
    sessionStorage.removeItem('briefed-legal-disclaimers-accepted-session');
    return signOut(auth);
  }

  // Reset password
  function resetPassword(email) {
    if (!auth) throw new Error('Firebase not configured');
    return sendPasswordResetEmail(auth, email);
  }

  // Send email verification
  async function sendVerificationEmail() {
    if (!auth || !currentUser)
      throw new Error('Firebase not configured or user not logged in');

    // Get the original Firebase user object (not the merged one)
    const firebaseUser = auth.currentUser;
    if (!firebaseUser) {
      throw new Error('No Firebase user found');
    }

    if (firebaseUser.emailVerified) {
      throw new Error('Email is already verified');
    }
    return sendEmailVerification(firebaseUser);
  }

  // Reload user to check verification status
  async function reloadUser() {
    if (!auth || !currentUser)
      throw new Error('Firebase not configured or user not logged in');

    // Get the original Firebase user object (not the merged one)
    const firebaseUser = auth.currentUser;
    if (!firebaseUser) {
      throw new Error('No Firebase user found');
    }

    await firebaseUser.reload();
    // Trigger a state update by setting the current user again
    setCurrentUser(firebaseUser);
    return firebaseUser.emailVerified;
  }

  // Update user profile
  async function updateUserProfile(profileData) {
    if (!auth || !currentUser)
      throw new Error('Firebase not configured or user not logged in');

    // Get the original Firebase user object (not the merged one)
    const firebaseUser = auth.currentUser;
    if (!firebaseUser) {
      throw new Error('No Firebase user found');
    }

    // Update Firestore user document if available, otherwise use localStorage
    if (db) {
      try {
        const timeoutPromise = new Promise((_, reject) =>
          setTimeout(() => reject(new Error('Firestore timeout')), 5000)
        );

        const setDocPromise = setDoc(
          doc(db, 'users', firebaseUser.uid),
          profileData,
          {
            merge: true
          }
        );

        await Promise.race([setDocPromise, timeoutPromise]);

        // Reload user data after successful update
        if (firebaseUser) {
          try {
            const userDoc = await getDoc(doc(db, 'users', firebaseUser.uid));
            if (userDoc.exists()) {
              setUserData(userDoc.data());
              console.log(
                'AuthContext: Reloaded user data after update:',
                userDoc.data()
              );
            }
          } catch (reloadError) {
            console.warn('Error reloading user data:', reloadError);
          }
        }
      } catch (error) {
        console.warn('Error updating user data in Firestore:', error.message);
        throw error; // Re-throw to let the calling code handle it
      }
    }

    return true;
  }

  useEffect(() => {
    if (skipAuth) {
      console.log('AuthContext: Authentication bypassed for development');
      // Create a mock user for development
      const mockUser = {
        uid: 'dev-user-123',
        email: 'dev@localhost.com',
        displayName: 'Development User',
        emailVerified: true,
        providerData: []
      };
      const mockUserData = {
        role: 'Defense Attorney (Private Practice)',
        organization: 'Development Organization',
        firstName: 'Dev',
        lastName: 'User'
      };
      setCurrentUser(mockUser);
      setUserData(mockUserData);
      setLoading(false);
      setUserDataLoading(false);
      return;
    }

    if (!auth) {
      console.log('AuthContext: No auth available, setting loading to false');
      setLoading(false);
      return;
    }

    console.log('AuthContext: Initializing with auth and db:', {
      auth: !!auth,
      db: !!db
    });

    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      setCurrentUser(user);

      if (user) {
        if (db) {
          // Load user data from Firestore if available
          setUserDataLoading(true);
          try {
            console.log(
              'AuthContext: Loading user data from Firestore for user:',
              user.uid
            );
            // Add timeout to prevent hanging
            const timeoutPromise = new Promise((_, reject) =>
              setTimeout(() => reject(new Error('Firestore timeout')), 5000)
            );

            const userDocPromise = getDoc(doc(db, 'users', user.uid));
            const userDoc = await Promise.race([
              userDocPromise,
              timeoutPromise
            ]);

            if (userDoc.exists()) {
              const userData = userDoc.data();
              setUserData(userData);
            } else {
              setUserData(null);
            }
          } catch (error) {
            console.warn(
              'Error loading user data from Firestore:',
              error.message
            );
            setUserData(null);
          } finally {
            setUserDataLoading(false);
          }
        } else {
          setUserData(null);
          setUserDataLoading(false);
        }
      } else {
        setUserData(null);
      }

      setLoading(false);
    });

    return unsubscribe;
  }, [skipAuth]);

  // Merge currentUser with userData for easy access
  const mergedUser = currentUser ? { ...currentUser, ...userData } : null;

  // Check for pending navigation whenever userData changes
  React.useEffect(() => {
    if (pendingNavigation && userData && currentUser) {
      const attorneyRoles = [
        'Defense Attorney (Private Practice)',
        'Public Defender',
        'Prosecutor'
      ];
      const hasProfileData = !!(
        userData.role &&
        (attorneyRoles.includes(userData.role) || userData.organization)
      );

      if (hasProfileData) {
        // Use window.location for navigation to avoid React Router issues
        window.location.href = pendingNavigation;
        setPendingNavigation(null);
      }
    }
  }, [pendingNavigation, userData, currentUser]);

  const value = {
    currentUser: mergedUser,
    userData,
    signup,
    login,
    loginWithGoogle,
    logout,
    resetPassword,
    sendVerificationEmail,
    reloadUser,
    updateUserProfile,
    setPendingNav
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && !userDataLoading && children}
    </AuthContext.Provider>
  );
}

AuthProvider.propTypes = {
  children: PropTypes.node.isRequired
};
